import { useAppTranslation } from '@flowus/common/i18n';
import { MoreButtonList } from '@flowus/common/components/more-button-list';
import type { FC, MouseEvent } from 'react';
import { useMemo } from 'react';
import { message } from 'src/common/components/message';
import { useOpenModal } from 'src/common/components/next-modal';
import { isUnsafeUrl } from '@flowus/common/url';
import { useOpenBlockRightMenuList } from 'src/editor/editor/uikit/hover-menu/block-menu-list/hook';
import { useOpenImageLinkDialog } from 'src/editor/editor/uikit/hover-menu/block-menu-list/use-open-image-link-dialog';
import { uiActions } from 'src/redux/reducers/ui';
import { dispatch } from 'src/redux/store';
import type { ReadonlyProp } from 'src/redux/types';
import { AlignPanel } from '../align-panel';

export interface MoreMenuProps extends ReadonlyProp {
  externalLink?: boolean;
  hideDownload?: boolean;
  showAlign?: boolean;
  blockId: string;
  link?: string;
  onReplace: () => void;
  onDownload: () => void;
  onAddComment?: () => void;
}
/**
 * 更多菜单
 */
export const ImageMoreMenu: FC<MoreMenuProps> = (props) => {
  const { t } = useAppTranslation();
  const openBlockRightMenuList = useOpenBlockRightMenuList();
  const openModal = useOpenModal();
  const openImageLinkDialog = useOpenImageLinkDialog();

  const renderList = useMemo(
    () => [
      {
        title: t('common.alignment'),
        icon: 'IcAlignLeft',
        hidden: !props.showAlign,
        click: (event: MouseEvent) => {
          dispatch(uiActions.updateSelectBlocks([{ blockId: props.blockId }]));
          openModal.dropdown({
            placement: 'top',
            popcorn: event.currentTarget,
            content: ({ onCloseModal }) => (
              <AlignPanel blockIds={[props.blockId]} closeModal={onCloseModal} />
            ),
          });
        },
      },
      {
        title: t('editor.component.image.more_menu.edit_hyperlink'),
        icon: 'IcLink',
        click: (event: MouseEvent) => {
          openImageLinkDialog(props.blockId, event.currentTarget);
        },
      },
      {
        title: props.externalLink
          ? t('editor.component.image.more_menu.open_link')
          : t('common.menu'),
        icon: props.externalLink ? 'IcGlobeShare' : 'IcMore',
        click: (event: MouseEvent) => {
          if (props.externalLink) {
            if (isUnsafeUrl(props.link)) {
              message.error(t('components.image.external_link_invalid'));
              return;
            }
            window.open(props.link);
          } else {
            const blockNode = document.querySelector(`[data-block-id="${props.blockId}"]`);
            openBlockRightMenuList({
              menuPosition: event.currentTarget as HTMLElement,
              blockId: props.blockId,
              blockNode: blockNode as HTMLElement,
              placement: 'right-end',
              offset: [0, 8],
            });
          }
        },
      },
      {
        title: t('common.add_comment'),
        icon: 'IcMenuReply',
        click: props.onAddComment,
        hidden: !props.onAddComment,
      },
    ],
    [
      openBlockRightMenuList,
      openImageLinkDialog,
      openModal,
      props.blockId,
      props.externalLink,
      props.link,
      props.onAddComment,
      props.showAlign,
      t,
    ]
  );

  return <MoreButtonList renderList={renderList} />;
};
